import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import * as types from '../../../../constants/actionTypes';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { actions as projectActions } from '../../../../actions/projectActions';
import { actions as projectLocationActions } from '../../../../actions/projectLocationActions';
import { actions as projectManagerActions } from '../../../../actions/projectManagerActions';
import { DEFAULT_PAGE_SIZE, PROJECT_LOCATION_BULK_UPDATE_OPTIONS as BulkOptions } from '../../../../constants/constants';
import Icon from '../../../Common/Icon';
import Modal from '../../../Common/Modal/Modal';
import Notification from '../../../Common/Notification';
import LoadingIndicator from '../../../Common/LoadingIndicator/LoadingIndicator';
import MultiSelectBox from '../../../Common/MultiSelectBox/MultiSelectBox';
import './ProjectLocationsPage.scss';
import { getSearchParamSortFields } from '../../../../helpers/sortHelper';
import ColumnFiltersDrawer from './ColumnFiltersDrawer';
import AddToScopeDrawer from './AddToScopeDrawer';
import ProjectLocationsTable from './ProjectLocationsTable';
import ChangeProjectManagerDrawer from './ChangeProjectManagerDrawer';
import PushdownDrawer from './PushdownDrawer';
import ChangeZeroDateDrawer from './ChangeZeroDateDrawer/ChangeZeroDateDrawer';
import AddBaselineDrawer from './AddBaselineDrawer';
import DocumentTitle from 'react-document-title';
import Routes from '../../../../constants/routes';
import { REPORT_IDS } from '../../../../constants/constants';
import signalRService from '../../../../services/signalRService';

const CHARACTER_LIMIT_ABBREVIATIONS_MAXIMUM = 900;

export class ProjectLocationsPage extends React.Component {
	constructor(props, context) {
		super(props, context);

		this.state = {
			projectName: '',
			projectLocations: [],
			allProjectManagers: [],
			allBaselines: [],
			editProjectLocation: {},
			selectedProjectLocation: {},
			isColumnFiltersDrawerOpen: false,
			isAddToScopeDrawerOpen: false,
			isChangeProjectManagerDrawerOpen: false,
			isChangeZeroDateDrawerOpen: false,
			isPushdownDrawerOpen: false,
			isAddBaselineDrawerOpen: false,
			isBulkEditModeEnabled: false,
			allRowsChecked: false,
			checkedRows: [],
			columns: [],
			totalCount: 0,
			isLoading: false,
			isLoadingFormFilters: false,
			showRemoveScopeWarning: false,
			showChangeManagerWarning: false,
			showOnholdWarning: false,
			showZeroDateWarning: false,
			showRevokePushdownWarning: false,
			filters: {
				projectManagers: [],
				baselines: [],
				brands: [],
				continents: [],
				managementCompanies: [],
				areas: [],
				regions: [],
				types: [],
				countries: [],
				zeroDates: [],
			},
			searchParams: {
				projectKey: this.props.projectKey,
				areas: [],
				areaIds: [],
				brands: [],
				brandIds: [],
				continents: [],
				continentIds: [],
				countries: [],
				countryIds: [],
				generation: '',
				isActive: true,
				isOnHold: false,
				isPushedDown: { label: 'All', value: null },
				locationName: '',
				locationAbbreviations: '',
				managementCompanies: [],
				managementCompanyIds: [],
				projectManagers: [],
				projectManagerIds: [],
				baselines: [],
				baselineIds: [],
				regions: [],
				regionIds: [],
				types: [],
				typeIds: [],
				zeroDates: [],
				pageSize: DEFAULT_PAGE_SIZE,
				pageNumber: 1,
				sortBy: 'locationName',
				sortDir: 'asc',
			},
		};
	}

	async componentDidMount() {
		await Promise.all([this.loadFilters(), this.search()]);

		// Join a group based on subscription
		signalRService.joinGroup(this.props.projectKey);

		// Initialize the SignalR service and set up global event listeners
		signalRService.on('UpdateProjectLocationStatus', this.handlePushdownCompleted);
	}

	handlePushdownCompleted = (updatedProjectLocation) => {
		const normalizedLocation = {
			...updatedProjectLocation,
			baselines: Array.isArray(updatedProjectLocation.baselines)
				? updatedProjectLocation.baselines.map((baseline) =>
					typeof baseline === 'object' ? baseline.name || JSON.stringify(baseline) : baseline
				)
				: [],
		};

		this.setState((prevState) => {
			const updatedProjectLocations = prevState.projectLocations.map((location) =>
				location.id === normalizedLocation.id ? { ...location, ...normalizedLocation } : location
			);

			return { projectLocations: updatedProjectLocations };
		});
	};

	loadFilters = async () => {
		try {
			this.setState({ isLoading: true, allRowsChecked: false });
			const result = await this.props.projectLocationActions.loadProjectLocationListFilters(this.props.projectKey);
			const payload = result.payload;
			if (result.type === types.LOAD_PROJECTLOCATION_FILTERS_SUCCESS) {
				this.setState({
					...this.state,
					projectName: payload.projectName,
					filters: {
						...this.state.filters,
						projectManagers: payload.projectManagers,
						baselines: payload.baselines,
						zeroDates: payload.projectZeroDates,
						brands: payload.brands,
						continents: payload.continents,
						managementCompanies: payload.managementCompanies,
						regions: payload.regions,
						areas: payload.areas,
						types: payload.types,
						countries: payload.countries,
					},
				});
			} else if (result.type === types.LOAD_PROJECTLOCATION_FILTERS_FAILURE) {
				Notification('error', 'An Error Occurred', payload.message);
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
		} finally {
			this.setState({ isLoading: false });
		}
	};

	setInitialColumns = (initalColumns) => {
		this.setState({ columns: initalColumns });
	};

	buildSearchRequest = () => {
		let projectManagerIds = [];
		if (this.state.searchParams.projectManagers.length > 0) {
			projectManagerIds = this.state.searchParams.projectManagers.map((item) => {
				return item.id;
			});
		}

		let baselineIds = [];
		if (this.state.searchParams.baselines.length > 0) {
			baselineIds = this.state.searchParams.baselines.map((item) => {
				return item.id;
			});
		}

		let zeroDates = [];
		if (this.state.searchParams.zeroDates.length > 0) {
			zeroDates = this.state.searchParams.zeroDates.map((item) => {
				return item.name;
			});
		}

		let brandIds = [];
		if (this.state.searchParams.brands.length > 0) {
			brandIds = this.state.searchParams.brands.map((item) => {
				return item.id;
			});
		}

		let continentIds = [];
		if (this.state.searchParams.continents.length > 0) {
			continentIds = this.state.searchParams.continents.map((item) => {
				return item.id;
			});
		}

		let managementCompanyIds = [];
		if (this.state.searchParams.managementCompanies.length > 0) {
			managementCompanyIds = this.state.searchParams.managementCompanies.map((item) => {
				return item.id;
			});
		}

		let areaIds = [];
		if (this.state.searchParams.areas.length > 0) {
			areaIds = this.state.searchParams.areas.map((item) => {
				return item.id;
			});
		}

		let regionIds = [];
		if (this.state.searchParams.regions.length > 0) {
			regionIds = this.state.searchParams.regions.map((item) => {
				return item.id;
			});
		}

		let typeIds = [];
		if (this.state.searchParams.types.length > 0) {
			typeIds = this.state.searchParams.types.map((item) => {
				return item.id;
			});
		}

		let countryIds = [];
		if (this.state.searchParams.countries.length > 0) {
			countryIds = this.state.searchParams.countries.map((item) => {
				return item.id;
			});
		}

		let isOnHold =
			this.state.searchParams.isOnHold &&
			typeof this.state.searchParams.isOnHold === 'object' &&
			Object.prototype.hasOwnProperty.call(this.state.searchParams.isOnHold, 'value')
				? this.state.searchParams.isOnHold.value
				: this.state.searchParams.isOnHold;

		let isActive =
			this.state.searchParams.isActive &&
			typeof this.state.searchParams.isActive === 'object' &&
			Object.prototype.hasOwnProperty.call(this.state.searchParams.isActive, 'value')
				? this.state.searchParams.isActive.value
				: this.state.searchParams.isActive;

		let isPushedDown =
			this.state.searchParams.isPushedDown &&
			typeof this.state.searchParams.isPushedDown === 'object' &&
			Object.prototype.hasOwnProperty.call(this.state.searchParams.isPushedDown, 'value')
				? this.state.searchParams.isPushedDown.value
				: this.state.searchParams.isPushedDown;

		return {
			...this.state.searchParams,
			projectManagerIds: projectManagerIds,
			baselineIds: baselineIds,
			zeroDates: zeroDates,
			isOnHold: isOnHold,
			isActive: isActive,
			isPushedDown: isPushedDown,
			brandIds: brandIds,
			continentIds: continentIds,
			managementCompanyIds: managementCompanyIds,
			areaIds: areaIds,
			regionIds: regionIds,
			typeIds: typeIds,
			countryIds: countryIds,
			locationAbbreviations: this.abbreviationsToArray(this.state.searchParams.locationAbbreviations),
		};
	};

	//we want the user to have the option of searching by a comma-delimited or space-delimited list
	abbreviationsToArray = (abbreviations) => {
		if (!abbreviations) {
			return [];
		}

		if (abbreviations.includes(',')) {
			return abbreviations.split(',');
		}

		return abbreviations.split(' ');
	};

	isAbbreviationsValid = () => {
		if (
			this.state.searchParams.locationAbbreviations &&
			this.state.searchParams.locationAbbreviations.length > CHARACTER_LIMIT_ABBREVIATIONS_MAXIMUM
		) {
			return false;
		}

		return true;
	};

	search = async () => {
		try {
			this.setState({ isLoading: true });

			if (this.isAbbreviationsValid() === false) {
				Notification(
					'error',
					'Character Limit Exceeded',
					`The character limit of ${CHARACTER_LIMIT_ABBREVIATIONS_MAXIMUM} has been exceeded for abbreviations`
				);
				return;
			}

			const request = this.buildSearchRequest();
			const result = await this.props.projectLocationActions.searchProjectLocations(request);
			const payload = result.payload;
			if (result.type === types.SEARCH_PROJECTLOCATION_SUCCESS) {
				const projectLocations = payload.results.map((pl) => {
					const flattenedBaselines = pl.baselines.map((b) => b.name); //flatten baselines objects for datatable
					return {
						...pl,
						baselines: flattenedBaselines,
						isChecked: false,
					};
				});
				this.setState({
					projectLocations,
					totalCount: payload.totalCount,
					allRowsChecked: false,
				});
			} else if (result.type === types.SEARCH_PROJECTLOCATION_FAILURE) {
				Notification('error', 'An Error Occurred', payload.message);
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
		} finally {
			this.setState({ isLoading: false });
		}
	};

	loadAllProjectManagers = async () => {
		const request = {
			isActive: true,
			name: '',
			subscriptionId: this.props.subscriptionId,
			pageSize: 0, //to get all results
			sortBy: 'Name',
		};

		try {
			const response = await this.props.projectManagerActions.searchProjectManagers(request);
			if (response.type === types.SEARCH_PROJECTMANAGER_SUCCESS) {
				this.setState({ allProjectManagers: response.payload.results });
			} else if (response.type === types.SEARCH_PROJECTMANAGER_FAILURE) {
				Notification('error', 'An Error Occurred', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
		}
	};

	loadPushdownFilters = async () => {
		try {
			const response = await this.props.projectLocationActions.loadPushdownFilters(this.props.subscriptionId, this.props.projectKey);
			if (response.type === types.LOAD_PROJECTLOCATION_PUSHDOWN_FILTERS_SUCCESS) {
				this.setState({ allBaselines: response.payload.baselines });
			} else if (response.type === types.LOAD_PROJECTLOCATION_PUSHDOWN_FILTERS_FAILURE) {
				Notification('error', 'An Error Occurred', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
		}
	};

	updateOnHold = async (isOnHold) => {
		try {
			const response = await this.props.projectLocationActions.updateOnHold(
				this.state.projectLocations.filter((m) => m.isChecked).map((m) => m.id),
				this.props.projectKey,
				isOnHold
			);
			if (response.type === types.UPDATE_PROJECTLOCATION_ONHOLD_SUCCESS) {
				if (response.payload.skippedNonPushedLocations) {
					this.setState({ showOnholdWarning: true });
				}
				if (response.payload.changedSomeLocations) {
					Notification('success', 'Success', 'Locations updated successfully');
					this.search();
				}
			} else if (response.type === types.UPDATE_PROJECTLOCATION_ONHOLD_FAILURE) {
				Notification('error', 'An Error Occurred', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
		}
	};

	onPageChange = (pageSize, pageNumber) => {
		this.setState(
			{
				searchParams: {
					...this.state.searchParams,
					pageSize,
					pageNumber,
				},
			},
			() => this.search()
		);
	};

	updateSearchParams = (name, value, shouldSearch = true) => {
		this.setState({ searchParams: { ...this.state.searchParams, [name]: value } }, () => {
			if (shouldSearch) {
				this.search();
			}
		});
	};

	onAddScope = () => {
		this.setState({ isAddToScopeDrawerOpen: true });
	};

	onAddScopeCancel = () => {
		this.setState({ isAddToScopeDrawerOpen: false });
	};

	onAddScopeComplete = () => {
		this.setState({ isAddToScopeDrawerOpen: false }, this.search);
	};

	onChangeManager = async () => {
		if (this.assertRowSelected()) {
			// only get PMs if the array is empty so we only need to load them the first time the drawer is opened
			if (this.state.allProjectManagers.length === 0) {
				this.setState({ isChangeProjectManagerDrawerOpen: true, isLoadingFormFilters: true });
				await this.loadAllProjectManagers();
				this.setState({ isLoadingFormFilters: false });
			} else {
				this.setState({ isChangeProjectManagerDrawerOpen: true });
			}
		}
	};

	onChangeManagerComplete = (showChangeManagerWarning) => {
		this.setState({ showChangeManagerWarning: showChangeManagerWarning, isChangeProjectManagerDrawerOpen: false, allRowsChecked: false });
		this.search();
	};

	onChangeManagerCancel = () => {
		this.setState({ isChangeProjectManagerDrawerOpen: false });
	};

	filteredRowsSelected = (boolean, feature) => {
		let errorMsg = '';
		switch (feature) {
			case 'Pushdown':
				errorMsg = 'You cannot pushdown because one of the selected locations is already pushed down';
				break;
			case 'AddBaseline':
				errorMsg = 'Please select locations that have been pushed down to';
				break;
			default:
				return false;
		}
		for (let row of this.state.checkedRows) {
			if (row.isPushedDown === boolean) {
				Notification('error', 'Error', errorMsg);
				return false;
			}
		}
		return true;
	};

	getAdditionalBaselines = () => {
		//returning an array of Baselines that are not already pushed down to any of the selected locations
		//comparison is done by Baseline name: app does not allow duplicate baseline names in the same subscription
		let currentBaselines = this.state.checkedRows.map(({ baselines }) => baselines);
		currentBaselines = [...new Set(currentBaselines.flat())];
		return this.state.allBaselines.filter(({ name }) => !currentBaselines.includes(name));
	};

	onAddBaselineOpenDrawer = async () => {
		if (this.assertRowSelected() && this.filteredRowsSelected(false, 'AddBaseline')) {
			if (this.state.allBaselines.length === 0) {
				this.setState({ isAddBaselineDrawerOpen: true, isLoadingFormFilters: true });
				await this.loadPushdownFilters();
				this.setState({ isLoadingFormFilters: false });
			} else {
				this.setState({ isAddBaselineDrawerOpen: true });
			}
		}
	};

	onAddBaselineCancel = () => {
		this.setState({ isAddBaselineDrawerOpen: false });
	};

	onAddBaselineComplete = () => {
		this.setState({ isAddBaselineDrawerOpen: false, allRowsChecked: false });
		this.search();
	};

	onPushdownOpenDrawer = async () => {
		if (this.assertRowSelected() && this.filteredRowsSelected(true, 'Pushdown')) {
			if (this.state.allBaselines.length === 0 || this.state.allProjectManagers.length === 0) {
				this.setState({ isPushdownDrawerOpen: true, isLoadingFormFilters: true });

				await this.loadAllProjectManagers();
				await this.loadPushdownFilters();
				this.setState({ isLoadingFormFilters: false });
			} else {
				this.setState({ isPushdownDrawerOpen: true });
			}
		}
	};

	onPushdownCancel = () => {
		this.setState({ isPushdownDrawerOpen: false });
	};

	onPushdownComplete = () => {
		this.setState({ isPushdownDrawerOpen: false, allRowsChecked: false });
		this.search();
	};

	onChangeZeroDate = () => {
		if (this.assertRowSelected()) {
			this.setState({ isChangeZeroDateDrawerOpen: true });
		}
	};

	onChangeZeroDateComplete = (showZeroDateWarning) => {
		this.setState({ isChangeZeroDateDrawerOpen: false, allRowsChecked: false, showZeroDateWarning: showZeroDateWarning }, this.search);
	};
	onChangeZeroDateCancel = () => {
		this.setState({ isChangeZeroDateDrawerOpen: false });
	};

	offHold = () => {
		if (this.assertRowSelected()) {
			this.updateOnHold(false);
		}
	};

	onHold = () => {
		if (this.assertRowSelected()) {
			this.updateOnHold(true);
		}
	};

	onRevokePushdown = async () => {
		if (this.assertRowSelected()) {
			try {
				const response = await this.props.projectLocationActions.revokePushdown(
					this.state.projectLocations.filter((m) => m.isChecked).map((m) => m.id),
					this.props.projectKey
				);
				if (response.type === types.REVOKE_PUSHDOWN_PROJECTLOCATION_SUCCESS) {
					if (response.payload.skippedNonPushedLocations) {
						this.setState({ showRevokePushdownWarning: true });
					}
					if (response.payload.changedSomeLocations) {
						Notification('success', 'Success', 'Revoke Pushdown was successful');
						this.search();
					}
				} else if (response.type === types.REVOKE_PUSHDOWN_PROJECTLOCATION_FAILURE) {
					Notification('error', 'An Error Occurred', response.payload.message);
				}
			} catch (error) {
				Notification('error', 'An Error Occurred', error);
			}
		}
	};

	onClearFilters = () => {
		Notification('error', 'Error', 'Clear Filters: Not Implemented Yet');
	};

	onColumnsCancel = () => {
		this.setState({ isColumnFiltersDrawerOpen: false });
	};

	onColumnsClick = () => {
		this.setState({ isColumnFiltersDrawerOpen: true });
	};

	onColumnsComplete = (columns) => {
		this.setState({ columns: columns, isColumnFiltersDrawerOpen: false });
	};

	onExport = () => {
		this.props.history.push({
			pathname: `${Routes.Reports}/${REPORT_IDS.ProjectLocationsList}`,
			state: { projectName: this.state.projectName },
		});
	};

	onMasterCheckboxToggle = () => {
		// Toggle the master checkbox to checked or unchecked
		const allRowsChecked = !this.state.allRowsChecked;

		// Check or uncheck all rows, based on the master checkbox's newly-toggled state
		const projectLocations = this.state.projectLocations.map((item) => {
			return { ...item, isChecked: allRowsChecked };
		});
		const checkedRows = allRowsChecked ? projectLocations.slice() : [];

		this.setState({
			checkedRows: checkedRows,
			projectLocations: projectLocations,
			allRowsChecked: allRowsChecked,
		});
	};

	assertRowSelected = () => {
		if (!this.state.checkedRows || !this.state.checkedRows.length) {
			Notification('error', 'Error', 'At least one row must be selected to perform a bulk operation');
			return false;
		}
		return true;
	};

	onRemoveFromScope = async () => {
		if (this.assertRowSelected()) {
			try {
				this.setState({ isLoading: true });
				var request = {
					subscriptionId: this.props.subscriptionId,
					projectKey: this.props.projectKey,
					projectLocationIds: this.state.checkedRows.map((x) => x.id),
				};
				const response = await this.props.projectLocationActions.removeFromScope(request);
				if (response.type === types.REMOVE_FROM_PROJECTLOCATION_SCOPE_SUCCESS) {
					if (response.payload.skippedPushedLocations) {
						this.setState({ showRemoveScopeWarning: true });
					}
					if (response.payload.removedSomeLocations) {
						Notification('success', 'Success', 'Remove from Scope completed successfully');
						this.search();
					}
				} else if (response.type === types.REMOVE_FROM_PROJECTLOCATION_SCOPE_FAILURE) {
					Notification('error', 'An Error Occurred', response.payload.message);
				}
			} catch (error) {
				Notification('error', 'Error', error);
			} finally {
				this.setState({ isLoading: false });
			}
		}
	};

	onSort = ({ sortField, sortOrder }) => {
		const { sortBy, sortDir } = getSearchParamSortFields(sortField, sortOrder);
		if (sortBy !== this.state.searchParams.sortBy || sortDir !== this.state.searchParams.sortDir) {
			this.setState(
				{
					searchParams: {
						...this.state.searchParams,
						sortBy: sortBy,
						sortDir: sortDir,
					},
				},
				this.search
			);
		}
	};

	onToggleCheckedRow = (projectLocationId) => {
		const projectLocations = this.state.projectLocations;
		let checkedRows = [];

		const newProjectLocations = projectLocations.map((item) => {
			let projectLocation = { ...item };
			if (item.id === projectLocationId) {
				projectLocation.isChecked = !item.isChecked;
			}

			if (projectLocation.isChecked) {
				checkedRows.push(projectLocation);
			}
			return projectLocation;
		});

		// If we've manually checked all tasks, also check the master checkbox
		const allRowsChecked = newProjectLocations.length === checkedRows.length;

		this.setState({
			checkedRows: checkedRows,
			projectLocations: newProjectLocations,
			allRowsChecked: allRowsChecked,
		});
	};

	selectProjectLocation = (e) => {
		const checkboxToggle =
			e.originalEvent.target.innerHTML.indexOf('p-checkbox-box') >= 0 || e.originalEvent.target.className.indexOf('p-checkbox-box') >= 0;
		if (checkboxToggle) {
			// Any part of the checkbox cell was clicked, so check the box instead of selecting the row
			this.onToggleCheckedRow(e.value.id);
		}
	};

	toggleBulkEditMode = () => {
		this.setState({ isBulkEditModeEnabled: !this.state.isBulkEditModeEnabled });
	};

	closeRemoveScopeWarning = () => {
		this.setState({ showRemoveScopeWarning: false, removeScopeWarningMessage: '' });
	};

	closeProjectManagerWarning = () => {
		this.setState({ showChangeManagerWarning: false });
	};

	closeOnholdWarning = () => {
		this.setState({ showOnholdWarning: false });
	};

	closeZeroDateWarning = () => {
		this.setState({ showZeroDateWarning: false });
	};

	closeRevokePushdownWarning = () => {
		this.setState({ showRevokePushdownWarning: false });
	};

	onViewChange = (viewName) => {
		switch (viewName) {
			case BulkOptions.BulkUpdate.value:
				break;
			case BulkOptions.AddBaseline.value:
				this.onAddBaselineOpenDrawer();
				break;
			case BulkOptions.Publish.value:
				this.onPushdownOpenDrawer();
				break;
			case BulkOptions.RemoveScope.value:
				this.onRemoveFromScope();
				break;
			case BulkOptions.ChangeManager.value:
				this.onChangeManager();
				break;
			case BulkOptions.ChangeZeroDate.value:
				this.onChangeZeroDate();
				break;
			case BulkOptions.Unpublish.value:
				this.onRevokePushdown();
				break;
			case BulkOptions.OnHold.value:
				this.onHold();
				break;
			case BulkOptions.OffHold.value:
				this.offHold();
				break;
			default:
				break;
		}
	};

	render() {
		const viewOptions = [
			{ id: BulkOptions.AddBaseline.value, name: BulkOptions.AddBaseline.label, icon: BulkOptions.AddBaseline.icon },
			{ id: BulkOptions.Publish.value, name: BulkOptions.Publish.label, icon: BulkOptions.Publish.icon },
			{ id: BulkOptions.RemoveScope.value, name: BulkOptions.RemoveScope.label, icon: BulkOptions.RemoveScope.icon },
			{ id: BulkOptions.ChangeManager.value, name: BulkOptions.ChangeManager.label, icon: BulkOptions.ChangeManager.icon },
			{ id: BulkOptions.ChangeZeroDate.value, name: BulkOptions.ChangeZeroDate.label, icon: BulkOptions.ChangeZeroDate.icon },
			{ id: BulkOptions.Unpublish.value, name: BulkOptions.Unpublish.label, icon: BulkOptions.Unpublish.icon },
			{ id: BulkOptions.OnHold.value, name: BulkOptions.OnHold.label, icon: BulkOptions.OnHold.icon },
			{ id: BulkOptions.OffHold.value, name: BulkOptions.OffHold.label, icon: BulkOptions.OffHold.icon },
		];

		const scopeWarningModalBody =
			'Some locations were not removed from scope because the project has been published to them. Before removing locations from scope, consider filtering the Status column to a value of Published.';
		const scopeWarningModalFooter = (
			<div>
				<button type='button' className='btn btn-primary' data-dismiss='modal' onClick={this.closeRemoveScopeWarning}>
					Ok
				</button>
			</div>
		);

		const managerWarningModalBody =
			'Some locations were not updated because the project has not been pushed to them. Before changing managers, consider filtering the Pushed Down column to a value of Yes.';
		const managerWarningModalFooter = (
			<div>
				<button type='button' className='btn btn-primary' data-dismiss='modal' onClick={this.closeProjectManagerWarning}>
					Ok
				</button>
			</div>
		);

		const onholdWarningModalBody =
			'Some locations were not updated because the project has not been pushed to them. Before On/Off Hold, consider filtering the Pushed Down column to a value of Yes.';
		const onholdWarningModalFooter = (
			<div>
				<button type='button' className='btn btn-primary' data-dismiss='modal' onClick={this.closeOnholdWarning}>
					Ok
				</button>
			</div>
		);

		const zeroDateWarningModalBody =
			'Some locations were not updated because the project has not been pushed to them. Before changing Zero Date, consider filtering the Pushed Down column to a value of Yes.';
		const zeroDateWarningModalFooter = (
			<div>
				<button type='button' className='btn btn-primary' data-dismiss='modal' onClick={this.closeZeroDateWarning}>
					Ok
				</button>
			</div>
		);

		const revokePushdownWarningModalBody =
			'Some locations/teams were not updated because the project has not been published to them. Before unpublishing, consider filtering the Status column to a value of Published.';
		const revokePushdownWarningModalFooter = (
			<div>
				<button type='button' className='btn btn-primary' data-dismiss='modal' onClick={this.closeRevokePushdownWarning}>
					Ok
				</button>
			</div>
		);

		return (
			<LoadingIndicator isLoading={this.state.isLoading}>
				<DocumentTitle title='Admin' />
				<div className='project-location-page'>
					<div className='project-location-header'>
						<div className='toolbar col-md-12'>
							<div className='project-name-header'>{this.state.projectName}</div>
							<div className='toolbar-button bulk-update'>
								{
									<Fragment>
										<Fragment>
											<MultiSelectBox
												name='views'
												idField='id'
												valueField='name'
												placeholder='Bulk Update    '
												className={'view-selector ' + BulkOptions.AddBaseline.value}
												options={viewOptions}
												onChange={this.onViewChange}
												selectedValues={BulkOptions.BulkUpdate.value}
												isMulti={false}
												isSearchable={false}
											/>
										</Fragment>
									</Fragment>
								}
							</div>
							{
								<Fragment>
									<div className='toolbar-button clear-filters' onClick={this.onClearFilters}>
										<Icon name='filter' />
										Clear Filters
									</div>
									<div className='toolbar-button columns' onClick={this.onColumnsClick}>
										<Icon name='eye' />
										Columns
									</div>
									<div className='toolbar-button export' onClick={this.onExport}>
										<Icon name='cloud-download-alt' />
										Export
									</div>
								</Fragment>
							}
							{this.state.showRemoveScopeWarning && (
								<Modal
									id='pushedDown'
									title='Skipped Locations'
									body={scopeWarningModalBody}
									onCloseClick={this.closeRemoveScopeWarning}
									customFooter={scopeWarningModalFooter}
								/>
							)}
							{this.state.showChangeManagerWarning && (
								<Modal
									id='managerWarning'
									title='Skipped Locations'
									body={managerWarningModalBody}
									onCloseClick={this.closeProjectManagerWarning}
									customFooter={managerWarningModalFooter}
								/>
							)}
							{this.state.showOnholdWarning && (
								<Modal
									id='onholdWarning'
									title='Skipped Locations'
									body={onholdWarningModalBody}
									onCloseClick={this.closeOnholdWarning}
									customFooter={onholdWarningModalFooter}
								/>
							)}
							{this.state.showZeroDateWarning && (
								<Modal
									id='zeroDateWarning'
									title='Skipped Locations'
									body={zeroDateWarningModalBody}
									onCloseClick={this.closeZeroDateWarning}
									customFooter={zeroDateWarningModalFooter}
								/>
							)}
							{this.state.showRevokePushdownWarning && (
								<Modal
									id='revokePushdownWarning'
									title='Skipped Locations'
									body={revokePushdownWarningModalBody}
									onCloseClick={this.closeRevokePushdownWarning}
									customFooter={revokePushdownWarningModalFooter}
								/>
							)}
							<button type='button' className='btn btn-primary' onClick={this.onAddScope} alt='Add Scope'>
								<span className='me-2'>Add Scope</span>
							</button>
						</div>
					</div>
					<ProjectLocationsTable
						allRowsChecked={this.state.allRowsChecked}
						columns={this.state.columns}
						filters={this.state.filters}
						isBulkEditModeEnabled={this.state.isBulkEditModeEnabled}
						projectLocations={this.state.projectLocations}
						searchParams={this.state.searchParams}
						selectedProjectLocation={this.state.selectedProjectLocation}
						totalCount={this.state.totalCount}
						onMasterCheckboxToggle={this.onMasterCheckboxToggle}
						onSort={this.onSort}
						onToggleCheckedRow={this.onToggleCheckedRow}
						search={this.search}
						selectProjectLocation={this.selectProjectLocation}
						setInitialColumns={this.setInitialColumns}
						updateSearchParams={this.updateSearchParams}
						onPageChange={this.onPageChange}
						windowWidth={this.props.windowWidth}
					/>
				</div>
				{this.state.isColumnFiltersDrawerOpen && (
					<ColumnFiltersDrawer onCancel={this.onColumnsCancel} onComplete={this.onColumnsComplete} columns={this.state.columns} />
				)}
				{this.state.isAddToScopeDrawerOpen && (
					<AddToScopeDrawer
						subscriptionId={this.props.subscriptionId}
						projectKey={this.props.projectKey}
						onCancel={this.onAddScopeCancel}
						onComplete={this.onAddScopeComplete}
						projectLocationActions={this.props.projectLocationActions}
					/>
				)}
				{this.state.isChangeProjectManagerDrawerOpen && (
					<ChangeProjectManagerDrawer
						projectLocationActions={this.props.projectLocationActions}
						projectManagers={this.state.allProjectManagers}
						selectedProjectLocations={this.state.projectLocations.filter((m) => m.isChecked)}
						subscriptionId={this.props.subscriptionId}
						projectKey={this.props.projectKey}
						onCancel={this.onChangeManagerCancel}
						onComplete={this.onChangeManagerComplete}
						isLoading={this.state.isLoadingFormFilters}
					/>
				)}
				{this.state.isPushdownDrawerOpen && (
					<PushdownDrawer
						projectLocationActions={this.props.projectLocationActions}
						projectActions={this.props.projectActions}
						projectManagers={this.state.allProjectManagers}
						baselines={this.state.allBaselines}
						selectedProjectLocations={this.state.projectLocations.filter((m) => m.isChecked)}
						subscriptionId={this.props.subscriptionId}
						projectKey={this.props.projectKey}
						projectName={this.state.projectName}
						onCancel={this.onPushdownCancel}
						onComplete={this.onPushdownComplete}
						isLoading={this.state.isLoadingFormFilters}
					/>
				)}

				{this.state.isAddBaselineDrawerOpen && (
					<AddBaselineDrawer
						projectLocationActions={this.props.projectLocationActions}
						projectActions={this.props.projectActions}
						baselines={this.getAdditionalBaselines()}
						selectedProjectLocations={this.state.projectLocations.filter((m) => m.isChecked)}
						subscriptionId={this.props.subscriptionId}
						projectKey={this.props.projectKey}
						onCancel={this.onAddBaselineCancel}
						onComplete={this.onAddBaselineComplete}
						isLoading={this.state.isLoadingFormFilters}
					/>
				)}

				{this.state.isChangeZeroDateDrawerOpen && (
					<ChangeZeroDateDrawer
						projectLocationActions={this.props.projectLocationActions}
						projectActions={this.props.projectActions}
						selectedProjectLocations={this.state.projectLocations.filter((m) => m.isChecked)}
						subscriptionId={this.props.subscriptionId}
						projectKey={this.props.projectKey}
						onCancel={this.onChangeZeroDateCancel}
						onComplete={this.onChangeZeroDateComplete}
					/>
				)}
			</LoadingIndicator>
		);
	}
}

ProjectLocationsPage.propTypes = {
	projectLocationActions: PropTypes.object.isRequired,
	projectManagerActions: PropTypes.object.isRequired,
	projectActions: PropTypes.object.isRequired,
	subscriptionId: PropTypes.string.isRequired,
	projectKey: PropTypes.string.isRequired,
	windowWidth: PropTypes.number.isRequired,
	history: PropTypes.object,
};

function mapStateToProps(state, ownProps) {
	let projectKey = '';
	if (ownProps.match.params.projectKey) {
		projectKey = ownProps.match.params.projectKey;
	}

	return {
		projectKey: projectKey,
		subscriptionId: state.user.currentSubscriptionId ? state.user.currentSubscriptionId.toString() : '',
		windowWidth: state.window.width,
	};
}

function mapDispatchToProps(dispatch) {
	return {
		projectLocationActions: bindActionCreators(projectLocationActions, dispatch),
		projectManagerActions: bindActionCreators(projectManagerActions, dispatch),
		projectActions: bindActionCreators(projectActions, dispatch),
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(ProjectLocationsPage);
